import { ErrorBoundary as ReactErrorBoundary, FallbackProps } from 'react-error-boundary'
import toast from 'react-hot-toast'
import { AxiosError } from 'axios'
import { Navigate, Outlet } from 'react-router-dom'

import { router } from './routes'

const is404Error = (error: Error): boolean => {
  const axiosError = error as AxiosError
  return (
    error.message.includes('404') ||
    axiosError?.response?.status === 404 ||
    axiosError?.status === 404 ||
    error.name === 'NotFoundError'
  )
}

const ErrorFallback = ({ error, resetErrorBoundary }: FallbackProps) => {
  console.error('Error caught by ErrorBoundary:', error)

  return (
    <div className="flex h-screen w-full flex-col items-center justify-center p-4">
      <div className="flex flex-col items-center gap-6 rounded-xl bg-white/5 p-8 shadow-lg backdrop-blur-sm">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-white">Oops!</h1>
          <p className="mt-2 text-xl text-white/80">Something went wrong</p>
          <p className="mt-1 text-white/60">We're having trouble processing your request. Please try again later.</p>
          <button
            onClick={resetErrorBoundary}
            className="mt-6 rounded-md bg-neutral-700 px-4 py-2 text-sm/6 font-semibold text-white shadow-inner shadow-white/10 transition-colors hover:bg-neutral-600"
          >
            Try again
          </button>
        </div>
      </div>
    </div>
  )
}

const NotFoundFallback = () => {
  return <Navigate to="/not-found" />
}

const AppErrorBoundary = () => {
  const handleError = (error: Error) => {
    console.error('Error caught by ErrorBoundary:', error)

    if (!is404Error(error)) {
      toast.error('Something went wrong. Please try again later.')
    }
  }

  const handleReset = () => {
    router.navigate('/')
  }

  return (
    <ReactErrorBoundary
      FallbackComponent={({ error, ...props }) => {
        return is404Error(error) ? <NotFoundFallback /> : <ErrorFallback error={error} {...props} />
      }}
      onError={handleError}
      onReset={handleReset}
    >
      <Outlet />
    </ReactErrorBoundary>
  )
}

export default AppErrorBoundary
